import { TFunction } from 'i18next';
import { BlocksIcon, BookMarkedIcon, BoxIcon, HouseIcon, ZapIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { NavLink, useLocation } from 'react-router';

import IfIcon from '@/components/atoms/Icons/IfIcon';
import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
} from '@/components/ui/navigation-menu';
import { useDeskId } from '@/hooks/useDeskId';
import { selectAccountSlug } from '@/redux/session/selectors';

import { matchesPath } from '../MainNavigation/utils';

const navItemClass =
  'h-10 w-16 px-3 py-2.5 hover:bg-nav-hover data-[active=true]:bg-nav-hover data-[active=true]:text-primary';

const navigationItems = (deskId: string, t: TFunction) => [
  { title: t('main_navigation.home'), path: '/', icon: <HouseIcon size={16} /> },
  { title: t('common.ai_agents'), path: '/ai-agents', icon: <ZapIcon size={16} /> },
  {
    title: t('ai_agents.navigation.knowledge'),
    path: '/knowledge-bases',
    icon: <BookMarkedIcon size={16} />,
  },
  {
    title: t('main_navigation.integrations', { count: 1 }),
    path: `/environments/${deskId}/integrations`,
    icon: <BlocksIcon size={16} />,
  },
  {
    title: t('main_navigation.rules', { count: 1 }),
    path: `/environments/${deskId}/rules`,
    icon: <IfIcon color="currentColor" size={16} />,
  },
  {
    title: t('main_navigation.environments', { count: 1 }),
    path: `/environments/${deskId}/settings`,
    icon: <BoxIcon size={16} />,
  },
];

const MainNavigationIframe = () => {
  const deskId = useDeskId();
  const { t } = useTranslation();

  const { pathname } = useLocation();
  const slug = useSelector(selectAccountSlug);

  return (
    <nav className="flex h-(--newheader-height) gap-1 bg-nav-band px-8">
      <NavigationMenu className="justify-start">
        <NavigationMenuList className="flex items-center gap-1">
          {navigationItems(deskId, t).map((item) => {
            const isActive = !!(item.path && matchesPath(pathname, item.path, slug));

            return (
              <NavigationMenuItem key={item.title}>
                <InfoTooltip content={item.title} side="bottom" delayDuration={200}>
                  <span>
                    <NavigationMenuLink asChild className={navItemClass}>
                      <NavLink to={`/${slug}${item.path}`} data-active={isActive}>
                        {item.icon}
                      </NavLink>
                    </NavigationMenuLink>
                  </span>
                </InfoTooltip>
              </NavigationMenuItem>
            );
          })}
        </NavigationMenuList>
      </NavigationMenu>
    </nav>
  );
};

export default MainNavigationIframe;
